import React from "react";

import { doNothing } from "@kazoohr/helpers";
import { SetStateFn } from "@kazoohr/types";

import { Checkbox } from "../Checkbox";
import { Spacer } from "../Grid";
import { Heading } from "../Heading";
import { IconButton } from "../IconButton";
import { Text } from "../Text";

import styles from "./SelectAndSort.scss";

export type SelectAndSortItem<T> = {
  id: T;
  label: string;
};

export type SelectAndSortProps<T extends string> = {
  fixedItems: SelectAndSortItem<T>[];
  totalItemOptions: SelectAndSortItem<T>[];
  selectedItemOptions: SelectAndSortItem<T>[];
  setSelectedItemOptions: (value: any) => void;
  headerText?: string;
  helperText?: string;
  maxSelectable: number;
  errorMessage: string;
  showError: boolean;
  setShowError: SetStateFn<boolean>;
};

export function SelectAndSort<T extends string>({
  fixedItems,
  totalItemOptions,
  selectedItemOptions,
  setSelectedItemOptions,
  headerText,
  helperText,
  maxSelectable,
  errorMessage,
  showError,
  setShowError,
}: SelectAndSortProps<T>) {
  return (
    <div>
      {headerText ? (
        <Heading level="3" className={styles["header-label"]}>
          {headerText}
        </Heading>
      ) : null}
      {helperText ? (
        <Text className={styles["info-text"]}>{helperText}</Text>
      ) : null}

      <ul className={styles["unordered-list-container"]}>
        {fixedItems.map((item) => (
          <li
            key={item.id}
            className={styles["fixed-list-item-clear-and-space"]}
          >
            <Row
              item={{
                ...item,
                id: item.id,
                isSelectable: false,
                isMoveableDown: false,
                isMoveableUp: false,
                checked: true,
              }}
              onSelect={doNothing}
            />
          </li>
        ))}
        {selectedItemOptions.map((item, index) => (
          <li
            key={item.id}
            className={styles["selected-list-item-clear-and-space"]}
          >
            <Row
              item={{
                ...item,
                id: item.id,
                isMoveableUp: index !== 0,
                isMoveableDown: index !== selectedItemOptions.length - 1,
                isSelectable: true,
                checked: true,
              }}
              onSelect={getOnSelectHandlerForSelectedItems(
                setShowError,
                setSelectedItemOptions
              )}
              onDown={getMoveItemDownHandler(item, setSelectedItemOptions)}
              onUp={getMoveItemUpHandler(item, setSelectedItemOptions)}
            />
          </li>
        ))}
        <Spacer orientation="vertical" size="small" />
        <Spacer orientation="vertical" size="tiny" />
        {filterSelectedItemsOutOfTotalOptionsAndAlphabetize(
          selectedItemOptions,

          totalItemOptions
        ).map((item) => (
          <li key={item.id} className={styles["list-item-clear-and-space"]}>
            <Row
              item={{
                ...item,
                id: item.id,
                isSelectable: true,
                checked: false,
                isMoveableUp: false,
                isMoveableDown: false,
              }}
              onSelect={getOnSelectHandlerForUnselectedItemOptions({
                selectedItemOptions,
                maxSelectable,
                setShowError,
                item,
                setSelectedItemOptions,
              })}
            />
          </li>
        ))}
      </ul>
      {showError ? (
        <Text className={styles["error-text"]}>{errorMessage}</Text>
      ) : null}
    </div>
  );
}

type ControllableItem<T> = {
  id: T;
  label: string;
  checked: boolean;
  isSelectable: boolean;
  isMoveableUp: boolean;
  isMoveableDown: boolean;
};

type RowProps<T> = {
  item: ControllableItem<T>;
  onSelect?: (item: SelectAndSortItem<T>) => void;
  onUp?: (item: SelectAndSortItem<T>) => void;
  onDown?: (item: SelectAndSortItem<T>) => void;
};

function Row<T extends string>({ item, onSelect, onUp, onDown }: RowProps<T>) {
  const { id, label, checked, isSelectable, isMoveableUp, isMoveableDown } =
    item;
  return (
    <div className={styles["list-item"]}>
      <Checkbox
        data-testid={`checkbox-${id}`}
        id={`checkbox-${id}`}
        label={label}
        onChange={getCheckboxOnChangeHandler(item, onSelect)}
        checked={checked}
        disabled={!isSelectable}
      />
      <div>
        {isSelectable && checked ? (
          <>
            <IconButton
              data-testid={`up-button-${id}`}
              className={styles["icon-button"]}
              icon="arrow_up"
              iconSize={12}
              iconVariant="regular"
              onClick={getOnClickHandler(item, onUp)}
              disabled={!isMoveableUp}
              containerSize={24}
            />
            <IconButton
              data-testid={`down-button-${id}`}
              className={styles["icon-button"]}
              icon="arrow_down"
              iconSize={12}
              iconVariant="regular"
              onClick={getOnClickHandler(item, onDown)}
              disabled={!isMoveableDown}
              containerSize={24}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}

export function getCheckboxOnChangeHandler<T>(
  item: SelectAndSortItem<T>,
  onSelect: ((item: SelectAndSortItem<T>) => void) | undefined
) {
  if (!onSelect) {
    return;
  }
  return function checkboxOnChangeHandler() {
    onSelect(item);
  };
}

export function getOnClickHandler<T>(
  item: SelectAndSortItem<T>,
  onClick: ((item: SelectAndSortItem<T>) => void) | undefined
) {
  if (!onClick) {
    return undefined;
  }
  return function onClickHandler() {
    onClick(item);
  };
}

export function reSortItemUpwardInArray<T>(
  array: SelectAndSortItem<T>[],
  index: number
) {
  if (index <= 0 || index >= array.length) {
    return array; // No change if index is out of bounds or already at the top
  }
  const newArray = array.slice();
  const temp = newArray[index - 1];
  newArray[index - 1] = newArray[index];
  newArray[index] = temp;
  return newArray;
}

export function reSortItemDownwardInArray<T>(
  array: SelectAndSortItem<T>[],
  index: number
) {
  if (index < 0 || index >= array.length - 1) {
    return array; // No change if index is out of bounds or already at the bottom
  }
  const newArray = array.slice();
  const temp = newArray[index + 1];
  newArray[index + 1] = newArray[index];
  newArray[index] = temp;
  return newArray;
}

export function getOnSelectHandlerForSelectedItems<T>(
  setShowError: SetStateFn<boolean>,
  setSelectedItemOptions: SetStateFn<SelectAndSortItem<T>[]>
) {
  return function onSelectHandlerForSelectedItems(item: SelectAndSortItem<T>) {
    setShowError(false);
    removeItemFromSelectedItemOptions(item, setSelectedItemOptions);
  };
}

type getOnSelectHandlerForUnselectedItemOptionsArgs<T> = {
  selectedItemOptions: SelectAndSortItem<T>[];
  maxSelectable: number;
  setShowError: SetStateFn<boolean>;
  item: SelectAndSortItem<T>;
  setSelectedItemOptions: SetStateFn<SelectAndSortItem<T>[]>;
};
export function getOnSelectHandlerForUnselectedItemOptions<T>(
  args: getOnSelectHandlerForUnselectedItemOptionsArgs<T>
) {
  const {
    selectedItemOptions,
    maxSelectable,
    setShowError,
    item,
    setSelectedItemOptions,
  } = args;
  return function onSelectHandlerForUnselectedItemOptions() {
    if (selectedItemOptions.length >= maxSelectable) {
      setShowError(true);
      return;
    }
    setSelectedItemOptions(
      getSetSelectedItemOptionsCallbackForAppendingToArray(item)
    );
  };
}

export function getSetSelectedItemOptionsCallbackForAppendingToArray<T>(
  item: SelectAndSortItem<T>
) {
  return function setSelectedItemOptionsCallbackForAppendingToArray<T>(
    prev: SelectAndSortItem<T>[]
  ) {
    return [...prev, item];
  };
}

export function removeItemFromSelectedItemOptions<T>(
  item: SelectAndSortItem<T>,
  setSelectedItemOptions: SetStateFn<SelectAndSortItem<T>[]>
) {
  setSelectedItemOptions(
    getSelectedItemOptionsCallbackForRemovingFromSelectedItemOptions(item)
  );
}

export function getSelectedItemOptionsCallbackForRemovingFromSelectedItemOptions<
  T
>(item: SelectAndSortItem<T>) {
  return function selectedItemOptionsCallbackForRemovingFromSelectedItemOptions(
    prev: SelectAndSortItem<T>[]
  ) {
    return prev.filter((i) => i.id !== item.id);
  };
}

export function filterSelectedItemsOutOfTotalOptionsAndAlphabetize<T>(
  selectedItemOptions: SelectAndSortItem<T>[],
  totalItemOptions: SelectAndSortItem<T>[]
) {
  // This filter will retain items that return true
  // .some will check if a condition is true and return true
  // if it does, we don't want to retain that item and will return false to the filter
  const arr = totalItemOptions.filter((totalItemOption) => {
    return !selectedItemOptions.some(
      (selectedItemOption) => selectedItemOption.id === totalItemOption.id
    );
  });

  return arr.sort((a, b) => a.label.localeCompare(b.label));
}

export function getMoveItemUpHandler<T>(
  item: SelectAndSortItem<T>,
  setSelectedItemOptions: SetStateFn<SelectAndSortItem<T>[]>
) {
  return function moveItemUpHandler() {
    setSelectedItemOptions(
      getSelectedItemOptionsCallbackForMovingItemUpwards(item)
    );
  };
}

export function getSelectedItemOptionsCallbackForMovingItemUpwards<T>(
  item: SelectAndSortItem<T>
) {
  return function selectedItemOptionsCallbackForMovingItemUpwards(
    prev: SelectAndSortItem<T>[]
  ) {
    const index = prev.findIndex((i) => i.id === item.id);
    return reSortItemUpwardInArray(prev, index);
  };
}

export function getMoveItemDownHandler<T>(
  item: SelectAndSortItem<T>,
  setSelectedItemOptions: SetStateFn<SelectAndSortItem<T>[]>
) {
  return function moveItemDownHandler() {
    setSelectedItemOptions(
      getSelectedItemOptionsCallbackForMovingItemDownward(item)
    );
  };
}

export function getSelectedItemOptionsCallbackForMovingItemDownward<T>(
  item: SelectAndSortItem<T>
) {
  return function selectedItemOptionsCallbackForMovingItemDownward(
    prev: SelectAndSortItem<T>[]
  ) {
    const index = prev.findIndex((i) => i.id === item.id);
    return reSortItemDownwardInArray(prev, index);
  };
}
